// Numbers, money, distances, durations and dates, formatted the way the app shows them.

import { type DistanceUnit, metersPerUnit, roundMoney } from "./rates.ts";

export const unitSymbol: Record<DistanceUnit, string> = { miles: "mi", kilometers: "km" };
export const speedSymbol: Record<DistanceUnit, string> = { miles: "mph", kilometers: "km/h" };

const locale = typeof navigator === "undefined" ? "en-US" : navigator.language;

export function inUnit(meters: number, unit: DistanceUnit): number {
  return meters / metersPerUnit[unit];
}

/** "12.4 mi" */
export function formatDistance(meters: number, unit: DistanceUnit): string {
  const value = inUnit(meters, unit);
  return `${value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${unitSymbol[unit]}`;
}

/** "40 mph" */
export function formatSpeed(metersPerSecond: number, unit: DistanceUnit): string {
  const perHour = (metersPerSecond * 3600) / metersPerUnit[unit];
  return `${Math.round(perHour).toLocaleString(locale)} ${speedSymbol[unit]}`;
}

/** "$18.70": to the cent, halves rounded up. */
export function formatMoney(value: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: currencyCode || "USD" }).format(roundMoney(value));
  } catch {
    return roundMoney(value).toFixed(2);
  }
}

/** "$0.725/mi": the rate with as many places as it has, up to three. */
export function formatRate(amount: number, currencyCode: string, unit: DistanceUnit): string {
  let text: string;
  try {
    text = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    }).format(amount);
  } catch {
    text = amount.toString();
  }
  return `${text}/${unitSymbol[unit]}`;
}

/** "41 min", "1 hr 5 min" */
export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}

/** "8:14 – 8:52 AM" */
export function formatTimeRange(start: Date, end: Date | null): string {
  if (!end) return formatTime(start);
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).formatRange(start, end);
}

/** "Fri, Oct 16" */
export function formatDayTitle(date: Date): string {
  return date.toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });
}

/** "October 2026" */
export function formatMonth(date: Date): string {
  return date.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

/** "October 16, 2026" */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" });
}

/** "Jul 1, 2026" */
export function formatShortDate(date: Date): string {
  return date.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
}

/** "today, 4:58 PM", "yesterday, 9:02 AM", or "Oct 12, 4:58 PM" */
export function formatRelativeMoment(date: Date, now: Date = new Date()): string {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((startOfToday.getTime() - startOfDay.getTime()) / 86_400_000);
  const time = formatTime(date);
  if (days === 0) return `today, ${time}`;
  if (days === 1) return `yesterday, ${time}`;
  return `${date.toLocaleDateString(locale, { month: "short", day: "numeric" })}, ${time}`;
}

export function formatCount(value: number): string {
  return value.toLocaleString(locale);
}

/** "1 trip", "1,248 trips" */
export function plural(count: number, one: string, many: string = `${one}s`): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/** "Business, Consulting, Medical and Charity" */
export function listText(items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(items);
  } catch {
    return items.join(", ");
  }
}
